import { addYears } from 'date-fns'

import type { FinCategory } from '#dictionary'
import { getRequiredDistributionAge } from '#dictionary/requiredDistributions'
import { AgeType, ValuePeriod } from '#domain/types'
import type MemberService from '#profile/MemberService'

import type { MemberRetirementAges } from '../memberRetirementAges'
import VALUE_PERIOD_MULTIPLIER from './valuePeriodMultiplier'

export type DateResolutionInput = {
  startAtDate: Date | null
  startAtAge: number | null
  endAtDate: Date | null
  endAtAge: number | null
  durationInPeriods: number | null
  valuePeriod: ValuePeriod
}

export type DateResolutionContext = {
  owner: MemberService
  finCategory: FinCategory
  retirementAges: MemberRetirementAges
}

export type ResolvedDates = {
  startAt: Date
  endAt: Date
}

const resolveAgeValue = (
  ageType: AgeType | null,
  owner: MemberService,
  retirementAges: MemberRetirementAges
): number | null => {
  const ownerAgeOnSubmission = owner.getAgeInTheEndOfYear(
    new Date().getFullYear()
  )

  switch (ageType) {
    case AgeType.AGE_NOW:
      return ownerAgeOnSubmission
    case AgeType.AGE_DEATH:
      return 100
    case AgeType.AGE_RETIREMENT: {
      const age = retirementAges[owner.id]
      if (age === undefined) {
        throw new Error(
          `retirementAges map is missing an entry for owner "${owner.id}"`
        )
      }
      return age
    }
    case AgeType.AGE_SOCIAL_SECURITY:
      return owner.socialSecurityAge
    case AgeType.AGE_REQUIRED_DISTRIBUTIONS:
      return getRequiredDistributionAge(owner.birthday)
    case AgeType.AGE_IN_A_YEAR:
      return ownerAgeOnSubmission + 1
    default:
      return null
  }
}

// TODO: we should count tail at the beginning and the end of the timeline
const durationToYears = (
  durationInPeriods: number,
  valuePeriod: ValuePeriod
): number => {
  const multiplier = VALUE_PERIOD_MULTIPLIER[valuePeriod] ?? 1
  return durationInPeriods / multiplier - 1
}

const isLastDayOfMonth = (date: Date): boolean => {
  const nextDay = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate() + 1
  )
  return nextDay.getMonth() !== date.getMonth()
}

// Nudge dates off month boundaries to avoid timezone shifts turning
// Jan 1 into Dec 31 (or vice versa) when the browser's local offset
// is applied. The calculator only uses the year, so ±1 day is harmless.
const safeDate = (date: Date): Date => {
  const day = date.getDate()
  if (day === 1) {
    return new Date(date.getFullYear(), date.getMonth(), 2)
  }
  if (isLastDayOfMonth(date)) {
    return new Date(date.getFullYear(), date.getMonth(), day - 1)
  }
  return date
}

const resolveDate = (
  date: Date | null,
  age: number | null,
  ageValue: number | null,
  owner: MemberService
): Date | null => {
  if (date) return date
  if (age) return new Date(owner.getYearForAge(age), 1, 15)
  if (ageValue) return new Date(owner.getYearForAge(ageValue), 1, 15)
  return null
}

const resolveEndAt = (
  input: DateResolutionInput,
  owner: MemberService,
  endAtAgeValue: number | null,
  startAt: Date
): Date | null => {
  const explicitEnd = resolveDate(input.endAtDate, input.endAtAge, null, owner)
  if (explicitEnd) return explicitEnd

  if (input.durationInPeriods) {
    const years = durationToYears(input.durationInPeriods, input.valuePeriod)
    return addYears(startAt, years)
  }

  if (endAtAgeValue) return new Date(owner.getYearForAge(endAtAgeValue), 1, 2)

  return null
}

const resolveRecordDates = (
  input: DateResolutionInput,
  context: DateResolutionContext
): ResolvedDates => {
  const { owner, finCategory, retirementAges } = context

  const startAtAgeValue = resolveAgeValue(
    finCategory.startAtAge,
    owner,
    retirementAges
  )
  const startAt = resolveDate(
    input.startAtDate,
    input.startAtAge,
    startAtAgeValue,
    owner
  )

  if (!startAt) {
    throw new Error('Failed to calculate date')
  }

  const endAtAgeValue = resolveAgeValue(
    finCategory.endAtAge,
    owner,
    retirementAges
  )
  const endAt = resolveEndAt(input, owner, endAtAgeValue, startAt)

  if (!endAt) {
    throw new Error('Failed to calculate date')
  }

  return { startAt: safeDate(startAt), endAt: safeDate(endAt) }
}

export default resolveRecordDates
